"use client";

import React, { useState } from "react";
import { Send } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "../ui/form";
import { QuoteRequestDialog } from "./QuoteRequestDialog";
import {
  QUOTE_FORM_CONFIG,
  FORM_FIELDS,
} from "@/utils/constants/forms";
import { THEME_COLORS } from "@/utils/constants/styles";
import { useQuoteRequestForm } from "@/hooks/useQuoteRequestForm";
import { Spinner } from "@/components/ui/shadcn-io/spinner";

interface QuoteRequestFormProps {
  title?: string;
  subtitle?: string;
  defaultService?: string;
  showPopupButton?: boolean;
  popupButtonText?: string;
  popupButtonVariant?: "default" | "outline" | "secondary" | "ghost" | "link" | "destructive";
  popupButtonClassName?: string;
  serviceOptions?: { value: string; label: string }[];
}

export function QuoteRequestForm({ 
  title = QUOTE_FORM_CONFIG.DEFAULT_TITLE,
  subtitle = QUOTE_FORM_CONFIG.DEFAULT_SUBTITLE,
  defaultService,
  showPopupButton = false,
  popupButtonText = QUOTE_FORM_CONFIG.DEFAULT_BUTTON_TEXT,
  popupButtonVariant = "default",
  popupButtonClassName = "",
  serviceOptions
}: Readonly<QuoteRequestFormProps>) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Se showPopupButton è true, mostra solo il pulsante che apre il popup
  if (showPopupButton) {
    return (
      <>
        <Button 
          onClick={() => setIsDialogOpen(true)}
          variant={popupButtonVariant}
          className={`${popupButtonClassName} transition-transform duration-150 active:scale-[0.97]`}
          aria-label={`Apri form per ${popupButtonText || 'richiedere preventivo'}`}
        >
          {popupButtonText}
        </Button>
        
        <QuoteRequestDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          title={title}
          subtitle={subtitle}
          defaultService={defaultService}
          serviceOptions={serviceOptions}
        />
      </>
    );
  }

  const {
    form,
    options,
    propertyTypeOptions,
    isSubmitting,
    isLoadingOptions,
    handleSubmit,
    handleServiceChange,
  } = useQuoteRequestForm({ defaultService, serviceOptions });

  // Altrimenti mostra il form normale (inline)
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader
        className={`bg-${THEME_COLORS.PRIMARY_GREEN} text-${THEME_COLORS.WHITE}`}
      >
        <CardTitle className="text-2xl">{title}</CardTitle>
        <p className="opacity-90">{subtitle}</p>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <Form {...form}>
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <input type="hidden" {...form.register("serviceName")} />
            <input type="hidden" {...form.register("redirectTo")} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder={FORM_FIELDS.PLACEHOLDERS.FIRST_NAME}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        placeholder={FORM_FIELDS.PLACEHOLDERS.LAST_NAME}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={FORM_FIELDS.PLACEHOLDERS.EMAIL}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder={FORM_FIELDS.PLACEHOLDERS.PHONE}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={FORM_FIELDS.PLACEHOLDERS.ADDRESS}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      placeholder={FORM_FIELDS.PLACEHOLDERS.CITY}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="serviceId"
              render={({ field }) => (
                <FormItem>
                  <Select
                    value={field.value || undefined}
                    disabled={isLoadingOptions}
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleServiceChange(value);
                    }}
                  >
                    <FormControl>
                      <SelectTrigger aria-label="Seleziona tipo di servizio">
                        <SelectValue
                          placeholder={
                            isLoadingOptions
                              ? "Caricamento servizi..."
                              : FORM_FIELDS.PLACEHOLDERS.SERVICE_TYPE
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="propertyType"
              render={({ field }) => (
                <FormItem>
                  <Select
                    value={field.value || undefined}
                    onValueChange={(value) => field.onChange(value)}
                  >
                    <FormControl>
                      <SelectTrigger aria-label="Seleziona tipo di proprietà">
                        <SelectValue
                          placeholder={FORM_FIELDS.PLACEHOLDERS.PROPERTY_TYPE}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {propertyTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="problemDescription"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Textarea
                      placeholder={FORM_FIELDS.PLACEHOLDERS.PROBLEM_DESCRIPTION}
                      className="min-h-32"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="privacyAccepted"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-start space-x-2">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={field.value}
                        onChange={(event) => field.onChange(event.target.checked)}
                      />
                    </FormControl>
                    <span className="text-sm text-muted-foreground">
                      {QUOTE_FORM_CONFIG.PRIVACY_TEXT}
                    </span>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-${THEME_COLORS.PRIMARY_GREEN} hover:bg-${THEME_COLORS.PRIMARY_GREEN_HOVER} text-${THEME_COLORS.WHITE} py-3 transition-transform duration-150 active:scale-[0.97]`}
            >
              {isSubmitting ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {isSubmitting ? "Invio in corso..." : QUOTE_FORM_CONFIG.SUBMIT_BUTTON_TEXT}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
